import { useState, useEffect, useRef } from "react";
import SessionStore from "../../../model/SessionStore";
import { DeviceData, DashboardWidgetConfig, LastMessage } from "../../../model/model";
import { Select, Modal, Button, Space, Card, InputNumber } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import GaugeComponent from "react-gauge-component";

interface GaugeWidgetProps {
  config: Partial<DashboardWidgetConfig>;
  onConfigChange: (config: Partial<DashboardWidgetConfig>) => void;
}

function GaugeWidget(props: GaugeWidgetProps) {
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | undefined>(props.config.deviceName);
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>(props.config.metric);
  const [selectedGaugeMin, setSelectedGaugeMin] = useState<number>(props.config.gaugeMin ?? 0);
  const [selectedGaugeMax, setSelectedGaugeMax] = useState<number | undefined>(props.config.gaugeMax);
  const [latestValue, setLatestValue] = useState<number | null>(null);
  const [availableMetrics, setAvailableMetrics] = useState<string[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    SessionStore.getClient().getAllDevices(SessionStore.getBearer()).then((resp) => {
      setDevices(resp.data);
    });
  }, []);

  useEffect(() => {
    if (!selectedDeviceName) {
      setAvailableMetrics([]);
      setSelectedMetric(undefined);
      return;
    }

    const device = devices.find(d => d.name === selectedDeviceName);
    if (!device || !device.id) {
      return;
    }

    setLoadingMetrics(true);
    SessionStore.getClient().suggestCols({ ds: device.id }, SessionStore.getBearer()).then((resp) => {
      setAvailableMetrics(resp.data || []);
      setLoadingMetrics(false);
    }).catch(() => {
      setAvailableMetrics([]);
      setLoadingMetrics(false);
    });
  }, [selectedDeviceName, devices]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!props.config.deviceName || !props.config.metric) {
      setLatestValue(null);
      return;
    }

    const device = devices.find(d => d.name === props.config.deviceName);
    if (!device || !device.id) {
      setLatestValue(null);
      return;
    }

    const fetchLastValue = () => {
      SessionStore.getClient().getDeviceLastMetric(device.id, SessionStore.getBearer())
        .then((resp) => {
          const lastMsg: LastMessage = resp.data;
          if (lastMsg?.values && props.config.metric && props.config.metric in lastMsg.values) {
            const val = lastMsg.values[props.config.metric];
            if (val !== null && val !== undefined) {
              setLatestValue(Number(val));
            } else {
              setLatestValue(null);
            }
          } else {
            setLatestValue(null);
          }
        })
        .catch(() => {
          setLatestValue(null);
        });
    };

    fetchLastValue();
    intervalRef.current = setInterval(fetchLastValue, 30000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [props.config.deviceName, props.config.metric, devices]);

  const handleSaveConfig = () => {
    const gaugeMax = selectedGaugeMax ?? Math.max(100, (latestValue ?? 0) * 2);
    props.onConfigChange({ deviceName: selectedDeviceName, metric: selectedMetric, gaugeMin: selectedGaugeMin, gaugeMax });
    setConfigModalVisible(false);
  };

  const renderConfigModal = () => (
    <Modal
      title="Gauge Widget Configuration"
      open={configModalVisible}
      onOk={handleSaveConfig}
      onCancel={() => setConfigModalVisible(false)}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <span>Device:</span>
        <Select
          style={{ width: '100%' }}
          value={selectedDeviceName}
          onChange={(val) => {
            setSelectedDeviceName(val);
            setSelectedMetric(undefined);
          }}
          placeholder="Select a device"
          options={devices.map((d: DeviceData) => ({ label: d.name, value: d.name }))}
        />
        {selectedDeviceName && (
          <>
            <span>Metric:</span>
            <Select
              style={{ width: '100%' }}
              value={selectedMetric}
              onChange={setSelectedMetric}
              placeholder={loadingMetrics ? "Loading metrics..." : "Select a metric"}
              loading={loadingMetrics}
              disabled={loadingMetrics || availableMetrics.length === 0}
              options={availableMetrics.map((m: string) => ({ label: m, value: m }))}
            />
            <span>Min Value:</span>
            <InputNumber
              style={{ width: '100%' }}
              value={selectedGaugeMin}
              onChange={(val) => setSelectedGaugeMin(val ?? 0)}
              placeholder="Default: 0"
            />
            <span>Max Value:</span>
            <InputNumber
              style={{ width: '100%' }}
              value={selectedGaugeMax}
              onChange={(val) => setSelectedGaugeMax(val ?? undefined)}
              placeholder="Default: current value x2"
            />
          </>
        )}
      </Space>
    </Modal>
  );

  if (!props.config.deviceName || !props.config.metric) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Button type="link" icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
          Configure Gauge
        </Button>
        {renderConfigModal()}
      </div>
    );
  }

  const displayValue = latestValue !== null ? latestValue : 0;
  const gaugeMin = props.config.gaugeMin ?? 0;
  const gaugeMax = props.config.gaugeMax ?? Math.max(100, displayValue * 1.2);

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', position: 'relative' }}>
      <Button
        size="small"
        icon={<SettingOutlined />}
        style={{ position: 'absolute', top: 0, right: 24, zIndex: 10 }}
        onClick={() => {
          setSelectedDeviceName(props.config.deviceName);
          setSelectedMetric(props.config.metric);
          setSelectedGaugeMin(props.config.gaugeMin ?? 0);
          setSelectedGaugeMax(props.config.gaugeMax);
          setConfigModalVisible(true);
        }}
      />
      <Card size="small" style={{ width: '100%' }}>
        <div style={{ width: '100%', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 8, top: 8, fontSize: 12, color: '#666', zIndex: 1 }}>
            {props.config.deviceName} - <strong>{props.config.metric}</strong>
          </span>
          <GaugeComponent
            type="semicircle"
            arc={{
              nbSubArcs: 100,
              colorArray: ['#FF5F6D', '#FFC371', '#36D986'],
              width: 0.3,
              padding: 0.005,
            }}
            value={displayValue}
            minValue={gaugeMin}
            maxValue={gaugeMax}
            pointer={{ type: "blob", animationDelay: 0 }}
            labels={{
              valueLabel: {
                style: { fontSize: 24 },
                formatTextValue: (value) => latestValue !== null ? `${value?.toFixed(2)}` : 'N/A',
              },
            }}
          />
        </div>
      </Card>
      {renderConfigModal()}
    </div>
  );
}

export default GaugeWidget;